import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';

import { apiFetch } from '@/lib/api/client';
import { queryKeys } from '@/lib/queryKeys';

/**
 * 현장 조직도 «판» (V379).
 *
 * <p>사용자가 넣어 둔 기본 양식(docs/design/조직도)을 그대로 세우고 고치는 자료다.
 * {@link useOrgChart}(직종별 묶음 · 정원 대비)와 묻는 것이 다르다 — 그쪽은 「몇 자리가 비었나」,
 * 이쪽은 「이 현장의 조직이 어떻게 생겼나」.
 *
 * <h2>통째로 저장한다</h2>
 * 조작 하나가 여러 줄을 건드린다(묶음을 지우면 자리가 함께 가고, 품질 등급을 바꾸면 자리 줄이
 * 다시 깔린다). 줄 단위로 쪼개면 그중 하나가 실패했을 때 화면과 DB 가 <b>반쯤 어긋난 채</b> 남고,
 * 그 상태를 되돌릴 방법이 사람에게 없다.
 */

export type OrgSlot = {
  /** 아직 저장되지 않은 줄은 비어 있다 — 서버가 저장할 때 새로 만든다. */
  id: string | null;
  title: string;
  sortOrder: number;
  personnelId: string | null;
  personName: string | null;
  personRank: string | null;
  /** 증명사진이 있는가 — 서버가 인사기록에서 읽어 준다. 화면에서 세운 줄은 저장 응답이 올 때까지 false 다. */
  hasPhoto: boolean;
  /**
   * 현장 연락처(공무)에 올리는가 (V418). 화면에서 새로 세운 줄은 비워 보낸다 — 서버가 자리 이름으로
   * 정한다(사무보조만 꺼짐).
   */
  contactPublic?: boolean;
};

export type OrgGroupKind = 'FREE' | 'QUALITY' | 'SAFETY';

export type OrgGroup = {
  /** 묶음은 화면에서 만들지 않는다(틀은 서버의 양식이 정한다) — 늘 서버가 준 id 다. */
  id: string | null;
  parentId: string | null;
  title: string;
  kind: OrgGroupKind;
  /** 품질등급 · 안전구간. null 이 「미지정」이고, 그때 자리 줄이 없다. */
  preset: string | null;
  gridCol: number;
  gridRow: number;
  sortOrder: number;
  slots: OrgSlot[];
};

/** 드롭박스 한 줄. **규칙은 서버가 들고 있다** — 화면에 베껴 두면 언젠가 한쪽만 고쳐진다. */
export type PresetOption = { value: string; label: string; slots: string[] };

export type OrgCandidate = {
  personnelId: string;
  name: string | null;
  rank: string | null;
  roleTitle: string | null;
  fieldName: string | null;
  /**
   * APPOINTEE — 배치 없이 이 현장의 법정 선임만 있는 사람(본사 소방안전관리자·안전관리자 등 ·
   * 사용자 지시 2026-10-14). 그때 {@code roleTitle} 은 선임 직명이다. 옛 서버는 이 값을 보내지 않는다.
   */
  assignmentRole: 'PRIMARY' | 'CONCURRENT' | 'DISPATCH' | 'APPOINTEE';
};

export type OrgLayout = {
  siteId: string;
  siteName: string;
  columns: number;
  contractAmount: string | null;
  /** 계약금액 글자에서 읽어 낸 원 단위 금액. **단위를 못 읽으면 null** 이다. */
  contractAmountKrw: number | null;
  groups: OrgGroup[];
  members: OrgCandidate[];
  qualityPresets: PresetOption[];
  safetyPresets: PresetOption[];
  /** 계약금액이 가리키는 안전 구간 — 제안이다. 금액을 모르면 null. */
  suggestedSafetyPreset: string | null;
  /**
   * 종결 현장에서 사람마다 머문 기간 — 칸 아래 「발령 · 전출」 (사용자 지시 2026-10-16). 칸이 아니라
   * 사람에 붙는다(옮겨 세워도 앞사람 날짜가 따라가지 않게). former 조회가 아니면 비어 있고, 옛 서버는 보내지 않는다.
   */
  tenures?: OrgTenure[];
};

export type OrgTenure = { personnelId: string; joinedOn: string | null; leftOn: string | null };

/**
 * @param former 종결 현장의 떠난 사람과 기간까지 — 인력배치표 › 조직도만 켠다. 현장 조회 · 직원찾기와
 *               판 모양이 달라 열쇠를 가른다. 무효화는 앞 두 칸만 주면 둘 다 걸린다.
 */
export function orgLayoutKey(siteId: string | null, former = false) {
  return ['org-layout', siteId, former] as const;
}

function orgLayoutUrl(siteId: string | null, former: boolean) {
  return `/v1/hr/sites/${siteId}/org-layout${former ? '?former=true' : ''}`;
}

export function useOrgLayout(siteId: string | null, former = false) {
  return useQuery({
    queryKey: orgLayoutKey(siteId, former),
    queryFn: () => apiFetch<OrgLayout>(orgLayoutUrl(siteId, former)),
    enabled: !!siteId,
  });
}

/**
 * 판 저장 — <b>화면을 먼저 바꾸고</b> 보낸다.
 *
 * <h2>실패하면 되돌린다</h2>
 * 화면을 먼저 바꾸는 이상 <b>실패했을 때 되돌리는 손</b>이 반드시 있어야 한다. 없으면 403 이
 * 떨어져도 배치한 사람이 그대로 서 있고, 화면은 저장된 적 없는 것을 저장됐다고 말한다. 되돌린
 * 뒤에 서버에서 다시 받아 온다 — 되돌린 값도 내 짐작이지 서버가 아는 값은 아니다.
 *
 * <h2>늦게 온 응답이 새 상태를 덮지 않는다</h2>
 * 빠르게 두 번 고치면 PUT 이 둘 날아가고 응답은 순서대로 오지 않는다. 번호표({@code ticket})를
 * 끊어 <b>마지막에 보낸 것</b>의 응답만 캐시에 꽂는다.
 */
export function useSaveOrgLayout(siteId: string | null, former = false) {
  const queryClient = useQueryClient();
  const issued = useRef(0);
  const key = orgLayoutKey(siteId, former);

  return useMutation({
    mutationFn: (groups: OrgGroup[]) =>
      apiFetch<OrgLayout>(orgLayoutUrl(siteId, former), {
        method: 'PUT',
        body: { groups },
      }),

    onMutate: async (groups) => {
      // 돌고 있는 조회가 내 낙관적 값을 덮어쓰지 못하게 먼저 세운다.
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<OrgLayout>(key);
      queryClient.setQueryData(key, (old: OrgLayout | undefined) =>
        old ? { ...old, groups } : old,
      );
      issued.current += 1;
      return { previous, ticket: issued.current };
    },

    onError: (_error, _groups, context) => {
      if (context?.previous) {
        queryClient.setQueryData(key, context.previous);
      }
      queryClient.invalidateQueries({ queryKey: key });
    },

    onSuccess: (saved, _groups, context) => {
      // 내가 마지막으로 보낸 것의 답일 때만 꽂는다. 아니면 더 새 요청이 곧 답한다.
      if (context?.ticket === issued.current) {
        queryClient.setQueryData(key, saved);
      }
      // 같은 현장의 다른 모양(현장 조회 · 직원찾기)도 다시 받게 한다.
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'org-layout' && query.queryKey[1] === siteId && query.queryKey[2] !== former,
      });
      // 배치 목록이 담당·연락처 공개(V418)를 판에서 읽어 온다 — 현장 개요의 연락처가 바로 따라오게.
      queryClient.invalidateQueries({ queryKey: queryKeys.personnelAssignments });
    },
  });
}

/**
 * 현장 조직도 변경 요청 (V452 · 사용자 지시 2026-10-14).
 *
 * <p>그 현장의 현장대리인은 현장 조회 › 조직도 탭에서 판을 고쳐 <b>요청</b>을 올리고, 인사배치 담당이
 * 승인하면 그 판으로 덮어쓴다. 현장당 대기 요청은 하나다 — 다시 올리면 앞의 것을 대신한다.
 */
export type OrgChartChange = {
  canPropose: boolean;
  canApprove: boolean;
  pending: {
    id: string;
    requesterName: string | null;
    requestedAt: string;
    groups: OrgGroup[];
  } | null;
};

function orgChangeKey(siteId: string | null) {
  return ['org-layout-change', siteId] as const;
}

/** 서버는 비어 있는 연락처 공개를 null 로 보낸다. 판은 「안 보냄」 으로 읽으므로 undefined 로 접는다. */
function normalizeChange(change: OrgChartChange): OrgChartChange {
  if (!change.pending) return change;
  return {
    ...change,
    pending: {
      ...change.pending,
      groups: change.pending.groups.map((group) => ({
        ...group,
        slots: group.slots.map((slot) => ({ ...slot, contactPublic: slot.contactPublic ?? undefined })),
      })),
    },
  };
}

export function useOrgChartChange(siteId: string | null) {
  return useQuery({
    queryKey: orgChangeKey(siteId),
    queryFn: () =>
      apiFetch<OrgChartChange>(`/v1/hr/sites/${siteId}/org-layout/change-request`).then(normalizeChange),
    enabled: !!siteId,
  });
}

/** 현장대리인의 [변경 요청]. 판은 그대로고 대기 요청만 선다. */
export function useSubmitOrgChange(siteId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (groups: OrgGroup[]) =>
      apiFetch<OrgChartChange>(`/v1/hr/sites/${siteId}/org-layout/change-request`, {
        method: 'POST',
        body: { groups },
      }).then(normalizeChange),
    onSuccess: (change) => queryClient.setQueryData(orgChangeKey(siteId), change),
  });
}

/** 인사배치 담당의 [승인]·[반려]. 승인은 요청한 판으로 덮어쓰므로 판도 다시 받는다. */
export function useDecideOrgChange(siteId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: 'approve' | 'reject' }) =>
      apiFetch<OrgChartChange>(`/v1/hr/org-layout/change-requests/${id}/${decision}`, {
        method: 'PUT',
      }).then(normalizeChange),
    onSuccess: (change) => {
      queryClient.setQueryData(orgChangeKey(siteId), change);
      queryClient.invalidateQueries({ queryKey: ['org-layout', siteId] });
      queryClient.invalidateQueries({ queryKey: ['org-chart'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.personnelAssignments });
    },
    onError: () => queryClient.invalidateQueries({ queryKey: orgChangeKey(siteId) }),
  });
}

/** 그 등급·구간이 세우는 자리 줄. 목록에 없는 값(미지정)은 자리를 만들지 않는다. */
export function slotsOfPreset(options: PresetOption[], value: string | null): string[] {
  return options.find((option) => option.value === value)?.slots ?? [];
}

/** 「824억」 처럼 읽기. 안전 구간이 왜 그렇게 깔렸는지를 사람이 눈으로 확인하는 값이다. */
export function formatKrw(krw: number | null): string | null {
  if (krw == null) return null;
  if (krw >= 1_0000_0000) {
    const eok = krw / 1_0000_0000;
    return `${Number.isInteger(eok) ? eok : eok.toFixed(1)}억`;
  }
  return `${krw.toLocaleString('ko-KR')}원`;
}
